import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  config.headers = { ...config.headers, ...getAuthHeader() };
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Users
export const getUsers = (params) => api.get('/users', { params });
export const getUsersDirectory = () => api.get('/users/directory');
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const updateUserSettings = (id, data) => api.put(`/users/${id}/settings`, data);
export const downloadEmployeeTemplate = () => api.get('/users/template/download', { responseType: 'blob' });
export const bulkImportUsers = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/users/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const approveTask = (id, data) => api.post(`/tasks/${id}/approval`, data);
export const getEligibleApprovers = (department) => api.get('/approvers/eligible', { params: { department } });
export const getDepartmentConfig = () => api.get('/tasks/department/config');
export const uploadAttachment = (taskId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/tasks/${taskId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const downloadAttachment = (taskId, attachmentId) => 
  api.get(`/tasks/${taskId}/attachments/${attachmentId}`, { responseType: 'blob' });
export const deleteAttachment = (taskId, attachmentId) => 
  api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
// S3 presigned-URL attachments
export const presignAttachment = (data) => api.post('/attachments/presign', data);
export const registerS3Attachment = (taskId, data) => api.post(`/tasks/${taskId}/attachments/s3`, data);
export const uploadToS3 = (uploadUrl, file, contentType) =>
  axios.put(uploadUrl, file, { headers: { 'Content-Type': contentType } });

// Notifications
export const getNotifications = () => api.get('/notifications');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Activity Logs
export const getActivityLogs = (params) => api.get('/activity-logs', { params });

// Dashboard
export const getDashboardStats = (params) => api.get('/dashboard/stats', { params });

// Organogram
export const getOrganogram = (department) => api.get('/organogram', { params: { department } });

// Backup
export const createBackup = () => api.post('/backup/manual');
export const getBackupHistory = () => api.get('/backup/history');
export const downloadBackup = (id) => api.get(`/backup/${id}/download`, { responseType: 'blob' });
export const deleteBackup = (id) => api.delete(`/backup/${id}`);
export const getBackupConfig = () => api.get('/backup/config');
export const updateBackupConfig = (data) => api.put('/backup/config', data);

// Export
export const exportTasks = (format, department) => 
  api.get('/export/tasks', { params: { format, department }, responseType: 'blob' });

// Options
export const getDepartments = () => api.get('/options/departments');
export const getRoles = () => api.get('/options/roles');
export const getCategories = () => api.get('/options/categories');
export const getDepartmentCategories = (department) => api.get('/categories', { params: { department } });
export const getBrands = () => api.get('/options/brands');
export const getStatuses = () => api.get('/options/statuses');

// Projects
export const getProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);

// ---- Uni AI Voice Assistant ----
export const uniAiTranscribe = (blob) => {
  const fd = new FormData();
  fd.append('file', blob, 'voice.webm');
  return api.post('/uni-ai/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uniAiChat = (message, sessionId, speak = true) => api.post('/uni-ai/chat', { message, session_id: sessionId, speak });
export const uniAiHistory = (sessionId) => api.get('/uni-ai/history', { params: { session_id: sessionId } });
export const uniAiClearHistory = (sessionId) => api.delete('/uni-ai/history', { params: { session_id: sessionId } });
export const deleteProject = (id) => api.delete(`/projects/${id}`);

// Task Comments
export const addTaskComment = (taskId, data) => api.post(`/tasks/${taskId}/comments`, data);
export const deleteTaskComment = (taskId, commentId) => api.delete(`/tasks/${taskId}/comments/${commentId}`);

// Workload
export const getWorkload = (department) => api.get('/workload', { params: { department } });

// Calendar
export const getCalendarTasks = (params) => api.get('/calendar/tasks', { params });

// Profile Image
export const uploadProfileImage = (userId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/users/${userId}/profile-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteProfileImage = (userId) => api.delete(`/users/${userId}/profile-image`);

// AI Assistant
export const aiChat = (data) => api.post('/ai/chat', data);
export const aiSuggestPriority = (data) => api.post('/ai/suggest-priority', data);
export const aiPredictDelays = () => api.post('/ai/predict-delays');
export const aiSummary = (data) => api.post('/ai/summary', data);
export const aiSearch = (data) => api.post('/ai/search', data);
export const aiChatHistory = () => api.get('/ai/history');

// Category Management
export const getManagedCategories = (department) => api.get('/categories/manage', { params: { department } });
export const createCategory = (data) => api.post('/categories/manage', data);
export const deleteCategory = (id) => api.delete(`/categories/manage/${id}`);

// Sub-Category Management
export const getSubcategories = (params) => api.get('/subcategories', { params });
export const getManagedSubcategories = (params) => api.get('/subcategories/manage', { params });
export const createSubcategory = (data) => api.post('/subcategories/manage', data);
export const deleteSubcategory = (id) => api.delete(`/subcategories/manage/${id}`);

// Super Admin user mgmt
export const impersonateUser = (userId) => api.post(`/auth/impersonate/${userId}`);
export const adminResetPassword = (userId, newPassword) => api.post(`/users/${userId}/reset-password`, { new_password: newPassword });

// Excel Backup
export const downloadExcelBackup = () => api.get('/backup/excel', { responseType: 'blob' });
export const testScheduledBackup = () => api.post('/backup/test-scheduled');

// Bulk Task Upload
export const downloadBulkTemplate = () => api.get('/tasks/bulk/template', { responseType: 'blob' });
export const bulkUploadTasks = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/tasks/bulk/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const quickCreateTasks = (tasks) => api.post('/tasks/bulk/quick', tasks);


// =================== THE FREQUENCY ===================
// Settings
export const getFrequencySettings = () => api.get('/frequency/settings');
export const updateFrequencySettings = (data) => api.put('/frequency/settings', data);
// Departments
export const getFrequencyDepartments = (activeOnly = false) => api.get('/frequency/departments', { params: { active_only: activeOnly } });
export const createFrequencyDepartment = (data) => api.post('/frequency/departments', data);
export const updateFrequencyDepartment = (id, data) => api.put(`/frequency/departments/${id}`, data);
export const deleteFrequencyDepartment = (id) => api.delete(`/frequency/departments/${id}`);
// Editions
export const getFrequencyEditions = (includeImmediate = false) => api.get('/frequency/editions', { params: { include_immediate: includeImmediate } });
export const getFrequencyEdition = (id) => api.get(`/frequency/editions/${id}`);
export const createFrequencyEdition = (data) => api.post('/frequency/editions', data);
export const updateFrequencyEdition = (id, data) => api.put(`/frequency/editions/${id}`, data);
export const deleteFrequencyEdition = (id) => api.delete(`/frequency/editions/${id}`);
export const previewFrequencyEdition = (id) => api.get(`/frequency/editions/${id}/preview`);
export const publishFrequencyEdition = (id, data = {}) => api.post(`/frequency/editions/${id}/publish`, data);
export const nudgeFrequencyEdition = (id, data) => api.post(`/frequency/editions/${id}/nudge`, data);
// Submissions
export const getFrequencySubmissions = (params = {}) => api.get('/frequency/submissions', { params });
export const createFrequencySubmission = (data) => api.post('/frequency/submissions', data);
export const updateFrequencySubmission = (id, data) => api.put(`/frequency/submissions/${id}`, data);
export const approveFrequencySubmission = (id) => api.post(`/frequency/submissions/${id}/approve`);
export const returnFrequencySubmission = (id, review_notes) => api.post(`/frequency/submissions/${id}/return`, { review_notes });
// Queries (auth)
export const getFrequencyQueries = () => api.get('/frequency/queries');
export const assignFrequencyQuery = (id, department_id) => api.put(`/frequency/queries/${id}/assign`, { department_id });
export const respondFrequencyQuery = (id, response_text) => api.post(`/frequency/queries/${id}/respond`, { response_text });
export const updateFrequencyQueryStatus = (id, status) => api.put(`/frequency/queries/${id}/status`, { status });
// Analytics
export const getFrequencyDashboardStats = (params = {}) => api.get('/frequency/dashboard/stats', { params });
export const getFrequencyEditionAnalytics = (id) => api.get(`/frequency/analytics/edition/${id}`);
// Magic-link (public, no auth)
const PUBLIC = axios.create({ baseURL: `${API_URL}/api/frequency` });
export const getFrequencyMagic = (token) => PUBLIC.get(`/magic/${token}`);
export const ackFrequencyMagic = (token) => PUBLIC.post(`/magic/${token}/ack`);
export const getFrequencyAccess = () => api.get('/frequency/access');
export const getFrequencySubmission = (id) => api.get(`/frequency/submissions/${id}`);

export const queryFrequencyMagic = (token, payload) => PUBLIC.post(`/magic/${token}/query`, payload);

// =================== ACCESS CONTROL (Module Access Matrix) ===================
export const getAccessModules = () => api.get('/access-control/modules');
export const getAccessCategories = () => api.get('/access-control/categories');
export const getAccessMatrix = () => api.get('/access-control/matrix');
export const saveAccessMatrix = (matrix) => api.put('/access-control/matrix', { matrix });
export const deleteAccessMatrixRow = (category) => api.delete(`/access-control/matrix/${encodeURIComponent(category)}`);
export const getMyAccess = () => api.get('/access-control/my-access');

// =================== FREQUENCY — RECIPIENTS & IMMEDIATE COMMS ===================
export const downloadFrequencyRecipientsTemplate = () => api.get('/frequency/recipients/template', { responseType: 'blob' });
export const uploadFrequencyRecipients = (editionId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/frequency/editions/${editionId}/recipients/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const clearFrequencyRecipients = (editionId) => api.delete(`/frequency/editions/${editionId}/recipients`);
export const sendFrequencyWhatsApp = (editionId) => api.post(`/frequency/editions/${editionId}/whatsapp`);
export const parseFrequencyRecipients = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/frequency/recipients/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const keepSubmissionForNewsletter = (subId) => api.post(`/frequency/submissions/${subId}/keep-for-newsletter`);
export const publishSubmissionImmediately = (subId, payload) => api.post(`/frequency/submissions/${subId}/publish-immediately`, payload);
export const getFrequencyImmediateAnalytics = () => api.get('/frequency/analytics/immediate');


// =================== FREQUENCY — COMMUNICATION DIRECTORY (Super Admin) ===================
export const getCommDirectory = (params = {}) => api.get('/frequency/directory', { params });
export const getCommDirectoryGroups = () => api.get('/frequency/directory/groups');
export const createCommDirectoryEntry = (data) => api.post('/frequency/directory', data);
export const updateCommDirectoryEntry = (id, data) => api.put(`/frequency/directory/${id}`, data);
export const deleteCommDirectoryEntry = (id) => api.delete(`/frequency/directory/${id}`);
export const downloadCommDirectoryTemplate = () => api.get('/frequency/directory/template', { responseType: 'blob' });
export const importCommDirectory = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/frequency/directory/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// =================== FREQUENCY — UPDATES (direct-publish communications) ===================
export const getFrequencyUpdates = () => api.get('/frequency/updates');
export const getFrequencyUpdate = (id) => api.get(`/frequency/updates/${id}`);
export const createFrequencyUpdate = (data) => api.post('/frequency/updates', data);
export const updateFrequencyUpdate = (id, data) => api.put(`/frequency/updates/${id}`, data);
export const deleteFrequencyUpdate = (id) => api.delete(`/frequency/updates/${id}`);
export const previewFrequencyUpdate = (id) => api.get(`/frequency/updates/${id}/preview`);
export const publishFrequencyUpdate = (id) => api.post(`/frequency/updates/${id}/publish`);
export const getFrequencyUpdatesAnalytics = () => api.get('/frequency/analytics/updates');
export const getFrequencyUpdateAnalytics = (id) => api.get(`/frequency/analytics/update/${id}`);
export const sendFrequencyTestReminder = (email) => api.post('/frequency/reminder/test', null, { params: email ? { email } : {} });
